import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API, { resolveMediaUrl, getBackendBaseUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  PaperPlaneTilt, Paperclip, X, UsersThree, MagnifyingGlass,
  CheckSquare, Image, VideoCamera, Plus, Trash, XCircle,
  Link, BookmarkSimple, FolderOpen, FloppyDisk,
  ArrowsOutSimple, ArrowsInSimple, CaretLeft, WhatsappLogo, TelegramLogo,
  CheckCircle, ArrowClockwise, DeviceMobile, ArrowCounterClockwise,
  Prohibit, ShieldCheck, ArrowSquareOut, Warning, Eye, PencilSimple, Check
} from '@phosphor-icons/react';
import {
  WhatsApp24hHeaderBadge,
  WhatsApp24hListBadge,
  WhatsApp24hExpiredAlert,
  useWhatsApp24hTimer
} from '../components/WhatsApp24hTimer';

const BEEP_FREQ = 880;
function playBeep() {
  try { const c = new (window.AudioContext || window.webkitAudioContext)(); const o = c.createOscillator(); const g = c.createGain(); o.connect(g); g.connect(c.destination); o.frequency.value = BEEP_FREQ; o.type = 'sine'; g.gain.value = 0.2; o.start(); o.stop(c.currentTime + 0.12); } catch (e) { /* silent */ }
}

function showNotification(title, body, source) {
  try {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: source === 'whatsapp' ? '/whatsapp-icon.png' : '/telegram-icon.png', tag: title, silent: false });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  } catch (e) { /* silent */ }
}

function ChatImageBubble({ mediaUrl, msg, isWA, isOut, setMediaPreview, onRetry }) {
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(Boolean(mediaUrl));
  const [attemptedFallback, setAttemptedFallback] = useState(false);

  useEffect(() => {
    setLoadError(false);
    setLoading(Boolean(mediaUrl));
    setAttemptedFallback(false);
  }, [mediaUrl]);

  const isWAKind = isWA || msg.source === 'whatsapp' || ('contact_id' in msg);
  const displayName = (!msg.file_name || msg.file_name.startsWith('?') || msg.file_name.includes('mid=')) ? (isWAKind ? 'Photo' : 'Image') : msg.file_name;
  const backendBase = getBackendBaseUrl();
  const fallbackProxy = (isWAKind && msg.id) ? (backendBase ? `${backendBase}/api/whatsapp/messages/${msg.id}/media` : `/api/whatsapp/messages/${msg.id}/media`) : null;
  const activeUrl = (loadError && !attemptedFallback && fallbackProxy && mediaUrl !== fallbackProxy) ? fallbackProxy : mediaUrl;

  const handleImageError = () => {
    if (!attemptedFallback && fallbackProxy && mediaUrl !== fallbackProxy) {
      setAttemptedFallback(true);
      setLoading(true);
      return;
    }
    setLoading(false);
    setLoadError(true);
  };

  if (!activeUrl || (loadError && attemptedFallback)) {
    return (
      <div className={`my-1.5 p-2.5 rounded-lg border border-dashed max-w-[280px] ${
        isWAKind
          ? 'bg-black/5 dark:bg-white/5 border-emerald-500/30'
          : isOut
            ? 'bg-white/10 border-white/30 text-white'
            : 'bg-black/5 dark:bg-white/5 border-blue-500/30'
      }`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
              isWAKind
                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                : isOut
                  ? 'bg-white/20 text-white'
                  : 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
            }`}>
              <Image size={20} weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate">{displayName}</p>
              <p className={`text-[10px] ${isOut && !isWAKind ? 'text-white/70' : 'text-muted-foreground'}`}>
                {loadError ? 'Image could not load' : 'Photo received'}
              </p>
            </div>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={() => onRetry(msg.id)}
              className={`text-[11px] font-semibold px-2 py-1 rounded flex items-center gap-1 shrink-0 ${
                isWAKind
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20'
                  : isOut
                    ? 'bg-white/20 text-white hover:bg-white/30'
                    : 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20'
              }`}
              title="Load / Retry Photo"
            >
              <ArrowClockwise size={12} />
              <span>Load</span>
            </button>
          )}
        </div>
        {mediaUrl && (
          <div className={`mt-1.5 pt-1.5 border-t flex items-center justify-between ${
            isOut && !isWAKind ? 'border-white/20' : 'border-black/10 dark:border-white/10'
          }`}>
            <a
              href={mediaUrl}
              target="_blank"
              rel="noreferrer"
              className={`text-[11px] font-semibold hover:underline flex items-center gap-0.5 ${
                isWAKind
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : isOut
                    ? 'text-white'
                    : 'text-primary'
              }`}
            >
              Open link <ArrowSquareOut size={11} />
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="relative group my-1 overflow-hidden rounded-lg cursor-pointer max-w-[280px] bg-black/5 dark:bg-white/5 min-h-[140px] flex items-center justify-center"
      onClick={() => setMediaPreview({ url: activeUrl, type: 'photo', name: displayName })}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center gap-1.5 text-xs text-muted-foreground bg-muted/40 animate-pulse">
          <Image size={18} className="animate-spin text-primary" />
          <span>Loading photo...</span>
        </div>
      )}
      <img
        src={activeUrl}
        alt="Photo"
        className={`rounded-lg max-w-full max-h-[300px] object-cover transition-all duration-200 group-hover:scale-105 ${loading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setLoading(false)}
        onError={handleImageError}
      />
      {!loading && (
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white">
          <Eye size={18} className="drop-shadow" />
          <span className="text-xs font-medium drop-shadow">View Photo</span>
        </div>
      )}
    </div>
  );
}

const getChatKey = (item) => {
  if (!item) return '';
  if (item.source === 'whatsapp' || item.contact_id) return `wa-${item.contact_id}`;
  const bid = item.bot_id || 'x';
  const tid = item.telegram_user_id ?? item._id ?? '';
  return `tg-${bid}-${tid}`;
};

const sortChatsWithUnreadFirst = (list, activeKey = null) => {
  return [...list].sort((a, b) => {
    const aKey = getChatKey(a);
    const bKey = getChatKey(b);
    const aUnread = (activeKey && aKey === activeKey) ? 0 : (a.unread || 0);
    const bUnread = (activeKey && bKey === activeKey) ? 0 : (b.unread || 0);
    const aHas = aUnread > 0 ? 1 : 0;
    const bHas = bUnread > 0 ? 1 : 0;
    if (aHas !== bHas) return bHas - aHas;
    return (b.last_time || '').localeCompare(a.last_time || '');
  });
};

export default function BotLiveChatPage() {
  const { selectedBot, bots, setBots: setGlobalBots } = useOutletContext() || {};
  const [channelFilter, setChannelFilter] = useState('all'); // all | telegram | whatsapp
  const [chatUsers, setChatUsers] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkText, setBulkText] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkMediaUrl, setBulkMediaUrl] = useState(null);
  const [bulkMediaType, setBulkMediaType] = useState(null);
  const [bulkButtons, setBulkButtons] = useState([]);
  const [bulkShowLabel, setBulkShowLabel] = useState(true);
  const [showBulkExtras, setShowBulkExtras] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkTemplates, setBulkTemplates] = useState([]);
  const [showBulkTemplates, setShowBulkTemplates] = useState(false);
  const [bulkSaveName, setBulkSaveName] = useState('');
  const [showBulkSave, setShowBulkSave] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'open';
  const [statusFilter, setStatusFilter] = useState(initialTab === 'assigned' ? 'assigned' : (initialTab === 'blocked' ? 'blocked' : 'open')); // open | closed | assigned | blocked
  const [assignedUnreadCount, setAssignedUnreadCount] = useState(0);
  const prevAssignedCountRef = useRef(0);
  const [recentlyAssignedToAdmin, setRecentlyAssignedToAdmin] = useState([]);
  const [blockingUser, setBlockingUser] = useState(false);
  const [unblockingBot, setUnblockingBot] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleLimit, setVisibleLimit] = useState(50);

  const activeUserRef = useRef(activeUser);
  activeUserRef.current = activeUser;
  const bulkModeRef = useRef(bulkMode);
  bulkModeRef.current = bulkMode;
  const hasHistoryPushedRef = useRef(false);

  // Handle phone / browser hardware back button and ESC key to return to chat list
  const handleBack = useCallback(() => {
    if (mediaPreview) {
      setMediaPreview(null);
      return;
    }
    if (showBulkTemplates) {
      setShowBulkTemplates(false);
      return;
    }
    if (showBulkSave) {
      setShowBulkSave(false);
      return;
    }
    if (activeUserRef.current) {
      setActiveUser(null);
      if (hasHistoryPushedRef.current) {
        window.history.back();
      }
      hasHistoryPushedRef.current = false;
    } else if (bulkModeRef.current) {
      setBulkMode(false);
      if (hasHistoryPushedRef.current) {
        window.history.back();
      }
      hasHistoryPushedRef.current = false;
    }
  }, [mediaPreview, showBulkTemplates, showBulkSave]);

  useEffect(() => {
    const handlePopState = () => {
      if (activeUserRef.current) {
        setActiveUser(null);
        hasHistoryPushedRef.current = false;
      } else if (bulkModeRef.current) {
        setBulkMode(false);
        hasHistoryPushedRef.current = false;
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Listen for ESC key globally to close media preview, modals, or active chat
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBack]);

  const fetchAssignedUnread = useCallback(() => {
    API.get('/chat/assigned-unread')
      .then(r => {
        const count = r.data.unread || 0;
        if (count > prevAssignedCountRef.current && prevAssignedCountRef.current > 0) {
          playBeep();
        }
        prevAssignedCountRef.current = count;
        setAssignedUnreadCount(count);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchAssignedUnread();
    const t = setInterval(fetchAssignedUnread, 5000);
    return () => clearInterval(t);
  }, [fetchAssignedUnread]);

  // Update statusFilter if search param changes
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'assigned') setStatusFilter('assigned');
    else if (t === 'closed') setStatusFilter('closed');
    else if (t === 'blocked') setStatusFilter('blocked');
    else setStatusFilter('open');
  }, [searchParams]);

  const [adminList, setAdminList] = useState([]); // List of staff for assignment
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const chatCacheRef = useRef(new Map()); // In-memory client cache for 0ms instant chat switching
  const { user } = useAuth();
  const canDeleteChat = user?.role === 'super_admin' || user?.permissions?.includes('delete_chat');
  const [confirmDeleteChatOpen, setConfirmDeleteChatOpen] = useState(false);
  const [deletingChat, setDeletingChat] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const bulkFileRef = useRef(null);
  const prevCountRef = useRef(0);
  const userScrolledUpRef = useRef(false);
  const justSentRef = useRef(false);

  const bot = (bots || []).find(b => b.id === (activeUser?.bot_id || selectedBot)) || (activeUser?.bot_username ? { bot_name: activeUser?.bot_name, bot_username: activeUser?.bot_username } : null);
  const isWA = activeUser?.source === 'whatsapp';
  const waTimer = useWhatsApp24hTimer(isWA ? (activeUser?.last_incoming_time || activeUser?.last_time) : null);

  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setIsEditingName(false);
    setEditNameValue(activeUser?.name || '');
  }, [activeUser]);

  const handleSaveContactName = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const newName = (editNameValue || '').trim();
    if (!newName) {
      toast.error('Contact name cannot be empty');
      return;
    }
    if (!activeUser) return;
    setSavingName(true);
    try {
      if (activeUser.source === 'whatsapp') {
        await API.put(`/whatsapp/contacts/${activeUser.contact_id}`, { name: newName });
      } else {
        const bid = activeUser.bot_id || selectedBot || (bots && bots[0]?.id);
        await API.post(`/bot-chat/${bid}/contacts/${activeUser.telegram_user_id}/rename`, { name: newName });
      }
      setActiveUser(prev => prev ? { ...prev, name: newName } : null);
      setChatUsers(prev => prev.map(u => {
        const isTarget = activeUser.source === 'whatsapp'
          ? (u.source === 'whatsapp' && u.contact_id === activeUser.contact_id)
          : (u.source !== 'whatsapp' && (u.telegram_user_id === activeUser.telegram_user_id || u._id === activeUser._id));
        return isTarget ? { ...u, name: newName } : u;
      }));
      setIsEditingName(false);
      toast.success('Contact name updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const adjustTextareaHeight = useCallback((element) => {
    const el = element || textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const newHeight = Math.min(Math.max(el.scrollHeight, 38), 120);
    el.style.height = `${newHeight}px`;
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [text, activeUser, adjustTextareaHeight]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Shift+Enter: line change / newline without sending
      if (e.shiftKey) {
        return;
      }
      // Enter without Shift: send message
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.nativeEvent?.isComposing) {
        e.preventDefault();
        sendReply();
      }
    }
  };

  const handleChatScroll = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    userScrolledUpRef.current = el.scrollHeight - el.scrollTop - el.clientHeight > 80;
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchUsers = useCallback(() => {
    const url = (selectedBot && channelFilter === 'telegram') ? `/bot-chat/${selectedBot}/users?status=${statusFilter}&limit=200` : `/chat/unified-users?status=${statusFilter}&limit=200`;
    API.get(url).then(r => {
      let u = r.data.users || [];
      if (statusFilter === 'assigned') {
        if (user?.role === 'staff') {
          u = u.filter(x => x.assigned_to_admin_id === user?.id || recentlyAssignedToAdmin.some(item => item.key === (x.source === 'whatsapp' ? `wa-${x.contact_id}` : `${x.bot_id}-${x.telegram_user_id}`)));
        } else if (user?.role === 'super_admin') {
          u = u.filter(x => Boolean(x.assigned_to_admin_id));
        } else {
          u = u.filter(x => x.assigned_to_admin_id === user?.id || adminList.some(a => a.id === x.assigned_to_admin_id) || Boolean(x.assigned_to_admin_id));
        }
      }
      if (channelFilter === 'telegram') {
        u = u.filter(x => x.source === 'telegram' || !x.source);
      } else if (channelFilter === 'whatsapp') {
        u = u.filter(x => x.source === 'whatsapp');
      }

      const currentActiveKey = activeUserRef.current ? getChatKey(activeUserRef.current) : null;

      if (currentActiveKey) {
        u = u.map(item => {
          return getChatKey(item) === currentActiveKey ? { ...item, unread: 0 } : item;
        });
      }

      const totalUnread = u.reduce((s, x) => s + (x.unread || 0), 0);
      if (totalUnread > prevCountRef.current && prevCountRef.current > 0) {
        playBeep();
        // Find the newest unread
        const newest = u.find(x => (x.unread || 0) > 0);
        if (newest) {
          showNotification(
            newest.source === 'whatsapp' ? `WhatsApp — ${newest.name}` : `Telegram — ${newest.name}`,
            newest.last_message || 'New message',
            newest.source,
          );
        }
      }
      prevCountRef.current = totalUnread;
      setChatUsers(sortChatsWithUnreadFirst(u, currentActiveKey));
    }).catch(() => {});
  }, [selectedBot, statusFilter, channelFilter, user?.id, user?.role, recentlyAssignedToAdmin, adminList]);

  // Client-side real-time instant filter
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return chatUsers;
    const q = searchQuery.toLowerCase().trim();
    return chatUsers.filter(u => {
      const name = (u.name || '').toLowerCase();
      const username = (u.username || '').toLowerCase();
      const phone = (u.phone || '').toLowerCase();
      const botName = (u.bot_name || u.bot_username || '').toLowerCase();
      const id = String(u.telegram_user_id || u.contact_id || '');
      return name.includes(q) || username.includes(q) || phone.includes(q) || botName.includes(q) || id.includes(q);
    });
  }, [chatUsers, searchQuery]);

  // Progressive rendering slice: only renders visible chats in DOM for 60fps instant UI
  const displayedUsers = useMemo(() => {
    return filteredUsers.slice(0, visibleLimit);
  }, [filteredUsers, visibleLimit]);

  const handleUserListScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      setVisibleLimit(prev => Math.min(filteredUsers.length, prev + 40));
    }
  }, [filteredUsers.length]);

  // Request notification permission once on mount
  useEffect(() => {
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch (e) { /* ignore */ }
  }, []);

  const fetchMessages = useCallback(() => {
    if (!activeUser) return;
    const targetChatKey = getChatKey(activeUser);

    if (activeUser.source === 'whatsapp') {
      const cid = activeUser.contact_id;
      API.get(`/whatsapp/messages/${cid}`).then(r => {
        const currentActiveKey = activeUserRef.current ? getChatKey(activeUserRef.current) : null;
        if (currentActiveKey !== targetChatKey) return;

        const serverMsgs = r.data.messages || [];
        setMessages(prev => {
          const serverIds = new Set(serverMsgs.map(m => m.id));
          // Strictly retain only optimistic messages that belong to THIS chat!
          const stillOptimistic = prev.filter(m => m._optimistic && m._chat_key === targetChatKey && !serverIds.has(m.id));
          const merged = [...serverMsgs, ...stillOptimistic];
          chatCacheRef.current.set(targetChatKey, merged);
          return merged;
        });
        setIsLoadingChat(false);
        fetchAssignedUnread();
        API.post(`/whatsapp/contacts/${cid}/read`).catch(() => {});
        window.dispatchEvent(new CustomEvent('chat-read'));
      }).catch(() => {
        setIsLoadingChat(false);
      });
      return;
    }
    const bid = activeUser.bot_id || selectedBot;
    const tgid = activeUser.telegram_user_id;
    if (!bid) return;
    API.get(`/bot-chat/${bid}/messages/${tgid}`).then(r => {
      const currentActiveKey = activeUserRef.current ? getChatKey(activeUserRef.current) : null;
      if (currentActiveKey !== targetChatKey) return;

      const serverMsgs = r.data.messages || [];
      setMessages(prev => {
        const serverTexts = new Set(serverMsgs.filter(m => m.direction === 'outgoing').map(m => (m.text || '').trim()));
        // Strictly retain only optimistic messages that belong to THIS chat!
        const stillOptimistic = prev.filter(m => m._optimistic && m._chat_key === targetChatKey && !serverTexts.has((m.text || '').trim()));
        const merged = [...serverMsgs, ...stillOptimistic];
        chatCacheRef.current.set(targetChatKey, merged);
        return merged;
      });
      setIsLoadingChat(false);
      fetchAssignedUnread();
      API.post(`/bot-chat/${bid}/read/${tgid}`).catch(() => {});
      window.dispatchEvent(new CustomEvent('chat-read'));
    }).catch(() => {
      setIsLoadingChat(false);
    });
  }, [selectedBot, activeUser, fetchAssignedUnread]);

  const fetchAdminList = useCallback(() => {
    API.get('/chat/assignable-users')
      .then(r => setAdminList(r.data.users || []))
      .catch(() => {
        API.get('/staff').then(r => setAdminList(r.data.staff || [])).catch(() => {});
      });
  }, []);

  useEffect(() => { fetchUsers(); fetchAdminList(); }, [fetchUsers, fetchAdminList]);
  useEffect(() => { if (activeUser) fetchMessages(); }, [activeUser, fetchMessages]);
  useEffect(() => {
    let usersInt = null;
    let msgInt = null;

    const startPolling = () => {
      if (document.hidden) return;
      if (!usersInt) usersInt = setInterval(fetchUsers, 3000);
      if (activeUser && !msgInt) msgInt = setInterval(fetchMessages, 1500);
    };

    const stopPolling = () => {
      if (usersInt) { clearInterval(usersInt); usersInt = null; }
      if (msgInt) { clearInterval(msgInt); msgInt = null; }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchUsers();
        if (activeUser) fetchMessages();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeUser, fetchUsers, fetchMessages]);

  useEffect(() => {
    if (justSentRef.current) { scrollToBottom(); justSentRef.current = false; return; }
    if (!userScrolledUpRef.current) scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => { userScrolledUpRef.current = false; scrollToBottom(); }, [activeUser, scrollToBottom]);

  // Hide hamburger when chat is active on mobile
  useEffect(() => {
    if (activeUser) document.body.classList.add('chat-open');
    else document.body.classList.remove('chat-open');
    return () => document.body.classList.remove('chat-open');
  }, [activeUser]);

  // Fullscreen: hide sidebar via DOM
  useEffect(() => {
    const sidebar = document.querySelector('[data-testid="app-layout"]');
    if (!sidebar) return;
    if (fullscreen) {
      sidebar.classList.add('livechat-fullscreen');
      document.body.style.overflow = 'hidden';
    } else {
      sidebar.classList.remove('livechat-fullscreen');
      document.body.style.overflow = '';
    }
    return () => { sidebar.classList.remove('livechat-fullscreen'); document.body.style.overflow = ''; };
  }, [fullscreen]);

  const sendReply = async (e) => {
    e?.preventDefault();
    if (!text?.trim() && !file) return;
    if (!activeUser) return;

    const targetUser = activeUser;
    const targetChatKey = getChatKey(targetUser);

    const txt = text?.trim();
    const localFile = file;
    setText(''); setFile(null);
    if (textareaRef.current) {
      textareaRef.current.style.height = '38px';
    }
    setSending(true);

    // Optimistic message
    const senderName = user?.name || user?.username || (user?.email ? user.email.split('@')[0] : 'You');
    const senderRole = user?.role || 'staff';
    const optimistic = {
      id: `local-${Date.now()}`,
      _chat_key: targetChatKey,
      direction: 'outgoing',
      text: txt || '',
      sent_by: user?.id,
      sent_by_name: senderName,
      sent_by_role: senderRole,
      sender_name: senderName,
      created_at: new Date().toISOString(),
      _optimistic: true,
      ...(targetUser.source === 'whatsapp'
        ? { type: localFile ? (localFile.type?.startsWith('image') ? 'image' : localFile.type?.startsWith('video') ? 'video' : localFile.type?.startsWith('audio') ? 'audio' : 'document') : 'text',
            media_url: localFile ? URL.createObjectURL(localFile) : null,
            contact_id: targetUser.contact_id }
        : { file_url: localFile ? URL.createObjectURL(localFile) : null,
            media_type: localFile ? (localFile.type?.startsWith('image') ? 'photo' : localFile.type?.startsWith('video') ? 'video' : localFile.type?.startsWith('audio') ? 'audio' : 'document') : null,
            file_name: localFile?.name,
            telegram_user_id: targetUser.telegram_user_id }),
    };

    const currentActiveKey = activeUserRef.current ? getChatKey(activeUserRef.current) : null;
    if (currentActiveKey === targetChatKey) {
      setMessages(prev => [...prev, optimistic]);
      justSentRef.current = true;
    }

    try {
      const fd = new FormData();
      if (txt) fd.append('text', txt);
      if (localFile) fd.append('file', localFile);
      if (targetUser.source === 'whatsapp') {
        await API.post(`/whatsapp/reply/${targetUser.contact_id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        const bid = targetUser.bot_id || selectedBot;
        if (!bid) throw new Error('No bot context');
        await API.post(`/bot-chat/${bid}/reply/${targetUser.telegram_user_id}`, fd);
      }
      fetchMessages();
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
      // Remove optimistic on failure
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
    } finally { setSending(false); }
  };

  const toggleContactStatus = async () => {
    if (!activeUser) return;
    const nextClosed = activeUser.status !== 'closed';
    try {
      if (activeUser.source === 'whatsapp') {
        await API.post(`/whatsapp/contacts/${activeUser.contact_id}/${nextClosed ? 'close' : 'reopen'}`);
      } else {
        const bid = activeUser.bot_id || selectedBot;
        await API.post(`/bot-chat/${bid}/contacts/${activeUser.telegram_user_id}/${nextClosed ? 'close' : 'reopen'}`);
      }
      toast.success(nextClosed ? 'Chat closed' : 'Chat reopened');
      setActiveUser(u => u ? { ...u, status: nextClosed ? 'closed' : 'open' } : null);
      fetchUsers();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteChat = async () => {
    const target = userToDelete || activeUser;
    if (!target) return;
    setDeletingChat(true);
    try {
      if (target.source === 'whatsapp') {
        await API.delete(`/whatsapp/contacts/${target.contact_id}`);
      } else {
        const bid = target.bot_id || selectedBot || (bots && bots[0]?.id);
        await API.delete(`/bot-chat/${bid}/chat/${target.telegram_user_id}`);
      }
      toast.success('Chat deleted permanently');
      setConfirmDeleteChatOpen(false);
      setUserToDelete(null);
      if (activeUser && (
        (target.source === 'whatsapp' && activeUser.contact_id === target.contact_id) ||
        (target.source !== 'whatsapp' && activeUser.telegram_user_id === target.telegram_user_id)
      )) {
        setActiveUser(null);
        setMessages([]);
      }
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete chat');
    } finally {
      setDeletingChat(false);
    }
  };

  const handleAssign = async (adminId) => {
    if (!activeUser) return;
    const prevAssignedId = activeUser.assigned_to_admin_id;
    const userKey = activeUser.source === 'whatsapp' ? `wa-${activeUser.contact_id}` : `${activeUser.bot_id || 'x'}-${activeUser.telegram_user_id}`;
    try {
      const fd = new FormData();
      fd.append('admin_id', adminId);
      if (activeUser.source === 'whatsapp') {
        await API.post(`/whatsapp/contacts/${activeUser.contact_id}/assign`, fd);
      } else {
        const bid = activeUser.bot_id || selectedBot;
        await API.post(`/bot-chat/${bid}/contacts/${activeUser.telegram_user_id}/assign`, fd);
      }
      setActiveUser(u => u ? { ...u, assigned_to_admin_id: adminId } : null);
      if (user?.role === 'staff') {
        if (adminId && adminId !== user.id) {
          setRecentlyAssignedToAdmin(prev => [...prev.filter(x => x.key !== userKey), { key: userKey, user: { ...activeUser, assigned_to_admin_id: adminId } }]);
          toast.success('Assigned to Admin', {
            action: {
              label: 'Rollback',
              onClick: () => handleAssign(user.id),
            },
            duration: 8000,
          });
        } else if (adminId === user.id) {
          setRecentlyAssignedToAdmin(prev => prev.filter(x => x.key !== userKey));
          toast.success(prevAssignedId ? 'Rolled back to you' : 'Assigned to you');
        } else {
          setRecentlyAssignedToAdmin(prev => prev.filter(x => x.key !== userKey));
          toast.success('Unassigned');
        }
      } else {
        toast.success(adminId ? 'Assigned' : 'Unassigned');
      }
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign'); }
  };

  const handleRetryMedia = async (msgId) => {
    if (!msgId) return;
    try {
      toast.info('Fetching media from server...');
      const { data } = await API.post(`/whatsapp/messages/${msgId}/retry-media`);
      if (data?.media_url) {
        toast.success('Media loaded successfully');
        setMessages(prev => prev.map(m => m.id === msgId ? {
          ...m,
          media_url: data.media_url,
          file_url: data.file_url || data.media_url,
          file_name: data.file_name || (m.file_name && !m.file_name.startsWith('?') && !m.file_name.includes('mid=') ? m.file_name : 'Photo.jpg'),
          has_media: true
        } : m));
      } else {
        toast.error('Media could not be retrieved from server');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Media load failed');
    }
  };

  const handleBlockActiveUser = async () => {
    if (!activeUser) return;
    const name = activeUser.name || 'this user';
    if (!window.confirm(`Are you sure you want to block ${name} as Super Admin? They will no longer be able to message.`)) return;
    setBlockingUser(true);
    try {
      if (activeUser.source === 'whatsapp') {
        await API.post(`/whatsapp/contacts/${activeUser.contact_id}/block`);
      } else {
        const bid = activeUser.bot_id || selectedBot;
        await API.post(`/bot-chat/${bid}/contacts/${activeUser.telegram_user_id}/block`);
      }
      toast.success(`${name} blocked successfully`);
      setActiveUser(u => u ? { ...u, is_blocked: true, status: 'blocked' } : null);
      setChatUsers(prev => prev.map(u => {
        const match = activeUser.source === 'whatsapp' ? u.contact_id === activeUser.contact_id : u.telegram_user_id === activeUser.telegram_user_id;
        return match ? { ...u, is_blocked: true, status: 'blocked' } : u;
      }));
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to block user');
    } finally {
      setBlockingUser(false);
    }
  };

  const handleUnblockActiveUser = async () => {
    if (!activeUser) return;
    const name = activeUser.name || 'this user';
    setBlockingUser(true);
    try {
      if (activeUser.source === 'whatsapp') {
        await API.post(`/whatsapp/contacts/${activeUser.contact_id}/unblock`);
      } else {
        const bid = activeUser.bot_id || selectedBot;
        await API.post(`/bot-chat/${bid}/contacts/${activeUser.telegram_user_id}/unblock`);
      }
      toast.success(`${name} unblocked successfully`);
      setActiveUser(u => u ? { ...u, is_blocked: false, status: 'open' } : null);
      setChatUsers(prev => prev.map(u => {
        const match = activeUser.source === 'whatsapp' ? u.contact_id === activeUser.contact_id : u.telegram_user_id === activeUser.telegram_user_id;
        return match ? { ...u, is_blocked: false, status: 'open' } : u;
      }));
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unblock user');
    } finally {
      setBlockingUser(false);
    }
  };

  const handleUnblockBot = async () => {
    if (!bot) return;
    setUnblockingBot(true);
    try {
      await API.post(`/bots/${bot.id}/unblock`);
      toast.success(`Bot @${bot.bot_username || bot.bot_name} unblocked`);
      if (bots && Array.isArray(bots) && typeof setGlobalBots === 'function') {
        setGlobalBots(bots.map(b => b.id === bot.id ? { ...b, is_blocked: false } : b));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unblock bot');
    } finally {
      setUnblockingBot(false);
    }
  };


  const toggleUserSelection = (uid) => {
    setSelectedUsers(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };
  const selectAllUsers = () => {
    if (selectedUsers.length === chatUsers.length) setSelectedUsers([]);
    else setSelectedUsers(chatUsers.map(u => u.telegram_user_id));
  };

  const sendBulkMessage = async () => {
    if ((!bulkText.trim() && !bulkFile && !bulkMediaUrl && bulkButtons.length === 0) || selectedUsers.length === 0) return;
    const bid = selectedBot || (chatUsers[0]?.bot_id);
    if (!bid) return toast.error('No bot context');
    setBulkSending(true);
    try {
      const fd = new FormData();
      fd.append('user_ids', JSON.stringify(selectedUsers));
      if (bulkText.trim()) fd.append('text', bulkText.trim());
      if (bulkFile) fd.append('file', bulkFile);
      else if (bulkMediaUrl) fd.append('media_url', bulkMediaUrl);
      if (bulkButtons.length > 0) fd.append('inline_buttons', JSON.stringify(bulkButtons));
      fd.append('show_button_label', bulkShowLabel.toString());
      const { data } = await API.post(`/bot-chat/${bid}/bulk-send`, fd);
      toast.success(`Sent to ${data.sent}/${data.total} users`);
      setBulkText(''); setBulkFile(null); setBulkMediaUrl(null); setBulkMediaType(null);
      setBulkButtons([]); setBulkShowLabel(true); setSelectedUsers([]); setBulkMode(false); setShowBulkExtras(false); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBulkSending(false); }
  };

  const fetchBulkTemplates = useCallback(() => {
    const bid = selectedBot || (bots && bots[0]?.id);
    if (!bid) return;
    API.get(`/bot-chat/${bid}/bulk-templates`).then(r => setBulkTemplates(r.data.templates || [])).catch(() => {});
  }, [selectedBot, bots]);
  useEffect(() => { if (bulkMode) fetchBulkTemplates(); }, [bulkMode, fetchBulkTemplates]);

  const saveBulkTemplate = async () => {
    if (!bulkSaveName.trim()) return toast.error('Enter a name');
    if (!bulkText.trim() && !bulkFile && bulkButtons.length === 0) return toast.error('Add content first');
    const bid = selectedBot || (bots && bots[0]?.id);
    if (!bid) return;
    try {
      const fd = new FormData();
      fd.append('name', bulkSaveName.trim());
      if (bulkText.trim()) fd.append('text', bulkText.trim());
      if (bulkFile) fd.append('media_file', bulkFile);
      if (bulkButtons.length > 0) fd.append('inline_buttons', JSON.stringify(bulkButtons));
      await API.post(`/bot-chat/${bid}/bulk-templates`, fd);
      toast.success('Template saved'); setBulkSaveName(''); setShowBulkSave(false); fetchBulkTemplates();
    } catch (err) { toast.error('Failed to save'); }
  };
  const loadBulkTemplate = (tpl) => {
    setBulkText(tpl.text || ''); setBulkButtons(tpl.inline_buttons || []);
    setBulkFile(null); setBulkMediaUrl(tpl.media_url || null); setBulkMediaType(tpl.media_type || null);
    setShowBulkExtras(true); setShowBulkTemplates(false); toast.success(`Loaded "${tpl.name}"`);
  };
  const deleteBulkTemplate = async (id) => {
    const bid = selectedBot || (bots && bots[0]?.id);
    if (!bid) return;
    try { await API.delete(`/bot-chat/${bid}/bulk-templates/${id}`); setBulkTemplates(prev => prev.filter(t => t.id !== id)); toast.success('Deleted'); } catch (e) { toast.error('Failed'); }
  };

  if (!selectedBot && (!bots || bots.length === 0)) {
    return (
      <div className="space-y-4" data-testid="bot-live-chat-page">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>All Live Chat</h1>
        <div className="border border-dashed border-border rounded-md p-8 sm:p-12 text-center text-muted-foreground">No bots available</div>
      </div>
    );
  }

  // Message bubble renderer (clean, authentic WhatsApp-style layout)
  const renderMessage = (msg) => {
    const isOut = msg.direction === 'outgoing';
    const isWaMsg = isWA || msg.source === 'whatsapp' || activeUser?.source === 'whatsapp' || ('contact_id' in msg) || ('waba_id' in msg);
    const waMsg = isWaMsg;

    // Normalize media fields - strictly only when real media exists
    const rawMedia = (msg.file_url || msg.media_url || '').trim();
    const mediaKind = (msg.media_type || msg.type || '').toLowerCase();
    const hasRealMedia = Boolean(rawMedia && rawMedia !== 'null' && rawMedia !== 'undefined');
    const mediaUrl = hasRealMedia ? resolveMediaUrl(rawMedia, msg.id) : null;
    const isImage = Boolean(mediaUrl) && (mediaKind === 'photo' || mediaKind === 'image' || mediaKind === 'gif' || mediaKind === 'sticker' || Boolean(msg.photo) || /\.(jpg|jpeg|png|webp|gif|svg|bmp)$/i.test(mediaUrl.split('?')[0]));
    const isVideo = Boolean(mediaUrl) && (mediaKind === 'video' || /\.(mp4|mov|webm|3gp)$/i.test(mediaUrl.split('?')[0]));
    const isAudio = Boolean(mediaUrl) && (mediaKind === 'audio' || mediaKind === 'voice' || /\.(mp3|ogg|wav|m4a|aac)$/i.test(mediaUrl.split('?')[0]));
    const isDoc = Boolean(mediaUrl) && !isImage && !isVideo && !isAudio && (mediaKind === 'document' || Boolean(msg.file_name) || /\.(pdf|doc|docx|xls|xlsx|txt|zip|rar)$/i.test(mediaUrl.split('?')[0]));

    const bubbleCls = waMsg
      ? (isOut ? 'bg-[#d9fdd3] dark:bg-[#005c4b] text-[#111b21] dark:text-[#e9edef] rounded-2xl rounded-tr-xs shadow-xs' : 'bg-white dark:bg-[#202c33] text-[#111b21] dark:text-[#e9edef] rounded-2xl rounded-tl-xs shadow-xs border border-black/5 dark:border-white/5')
      : (isOut ? 'bg-[#0088cc] text-white rounded-2xl rounded-tr-xs shadow-xs' : 'bg-white dark:bg-[#182533] text-foreground dark:text-[#f5f5f5] rounded-2xl rounded-tl-xs shadow-xs border border-border/50');

    const rawSender = msg.sent_by_name || msg.sender_name || (typeof msg.sent_by === 'string' && !msg.sent_by.includes('-') ? msg.sent_by : '');
    const cleanSenderName = rawSender.replace(/^[\s🤖👤]+/, '').trim() || (isOut ? 'Bot' : 'User');
    const isBotSender = msg.sent_by_role === 'bot' || msg.sent_by === 'bot_automation' || rawSender.includes('Bot') || rawSender.includes('🤖');

    const timeStr = msg.created_at?.replace('T', ' ').slice(11, 16) || '';
    const timeCls = waMsg
      ? (isOut ? 'text-[10.5px] text-gray-600 dark:text-emerald-200/80 font-normal' : 'text-[10.5px] text-gray-500 dark:text-gray-400 font-normal')
      : `text-[10.5px] ${isOut ? 'text-white/75' : 'text-muted-foreground'} font-normal`;

    const checkmarks = (
      <>
        {msg._optimistic ? (
          <span className="text-[9px] italic opacity-60">sending...</span>
        ) : isOut ? (
          <span className={`text-[11px] font-bold leading-none ${waMsg ? 'text-emerald-700 dark:text-emerald-300' : 'text-white/85'}`} title="Delivered">✓✓</span>
        ) : null}
      </>
    );

    const hasMedia = isImage || isVideo || isAudio || isDoc;
    const hasButtons = Boolean(msg.inline_buttons && msg.inline_buttons.length > 0);

    return (
      <div key={msg.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'} my-0.5 sm:my-1`}>
        <div className={`relative w-fit max-w-[85%] sm:max-w-[70%] ${bubbleCls} overflow-hidden ${msg._optimistic ? 'opacity-70' : ''}`}>
          {/* Subtle tag ONLY for automated bot messages - no bulky header on regular staff/operator messages */}
          {isOut && isBotSender && (
            <div className={`text-[10px] font-semibold tracking-wide px-2.5 pt-1.5 pb-0.5 select-none ${waMsg ? 'text-emerald-800 dark:text-emerald-300' : 'text-white/90'}`}>
              🤖 {cleanSenderName || 'Bot Automation'}
            </div>
          )}

          {/* Media: Image / Photo */}
          {isImage && (
            <div className="relative p-1">
              <ChatImageBubble
                mediaUrl={mediaUrl}
                msg={msg}
                isWA={waMsg}
                isOut={isOut}
                setMediaPreview={setMediaPreview}
                onRetry={waMsg ? handleRetryMedia : null}
              />
            </div>
          )}

          {/* Media: Video */}
          {isVideo && (
            <div className="relative p-1">
              <video src={mediaUrl} controls className="max-w-full max-h-[300px] min-w-[240px] rounded-lg" preload="metadata" />
            </div>
          )}

          {/* Media: Audio */}
          {isAudio && (
            <div className="px-2.5 pt-1.5">
              <audio src={mediaUrl} controls className="w-full max-w-[260px]" />
            </div>
          )}

          {/* Media: Real Document */}
          {isDoc && (
            <div className="px-2.5 pt-1.5">
              <a href={mediaUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 rounded-lg bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 hover:bg-black/10 transition-colors">
                <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                  {(msg.file_name || 'file').split('.').pop()?.toUpperCase()?.slice(0, 3) || 'DOC'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{msg.file_name || 'Document'}</p>
                  <p className="text-[10px] text-muted-foreground">Click to download</p>
                </div>
              </a>
            </div>
          )}

          {/* Text Content + Inline Timestamp (genuine WhatsApp wrap behavior) */}
          {msg.text && (
            <div className={`px-2.5 ${hasMedia ? 'pt-1' : isBotSender ? 'pt-0.5' : 'pt-1.5'} pb-1.5`}>
              <div className="flex flex-wrap items-end justify-between gap-x-2.5 gap-y-0.5">
                <span className="text-[13.5px] sm:text-[14px] leading-snug break-words whitespace-pre-wrap">{msg.text}</span>
                <div className="inline-flex items-center gap-1 select-none ml-auto shrink-0 self-end pb-0.5">
                  <span className={timeCls}>{timeStr}</span>
                  {checkmarks}
                </div>
              </div>
            </div>
          )}

          {/* If message has media but NO text, render footer timestamp */}
          {hasMedia && !msg.text && (
            <div className="px-2.5 pb-1 pt-0.5 flex items-center justify-end gap-1 select-none">
              <span className={timeCls}>{timeStr}</span>
              {checkmarks}
            </div>
          )}

          {/* Inline Buttons - Telegram style, compact and clean */}
          {hasButtons && (
            <div className="px-2 pb-1.5 pt-0.5 space-y-1">
              {msg.inline_buttons.map((btn, bi) => (
                btn.title && btn.url ? (
                  <a key={bi} href={btn.url} target="_blank" rel="noopener noreferrer"
                    className={`flex items-center justify-center gap-1 text-xs font-medium px-2.5 py-1 rounded border transition-colors ${isOut && !waMsg ? 'border-white/20 bg-white/10 text-white hover:bg-white/20' : 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10'}`}>
                    {btn.title} ↗
                  </a>
                ) : null
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const chatContent = (
    <>
      {/* Bot Blocked by Super Admin banner */}
      {bot?.is_blocked && (
        <div className="bg-red-600 text-white px-3.5 py-2 text-xs flex items-center justify-between gap-3 shadow-sm shrink-0 mb-2 rounded-md animate-pulse" data-testid="bot-blocked-banner">
          <div className="flex items-center gap-2 truncate">
            <Prohibit size={18} weight="bold" className="shrink-0 text-white" />
            <span className="truncate">
              <strong>BLOCKED BOT:</strong> Bot @{bot.bot_username || bot.bot_name} is currently blocked by Super Admin. Bot operations, broadcasts, and replies are suspended.
            </span>
          </div>
          {user?.role === 'super_admin' && (
            <button
              onClick={handleUnblockBot}
              disabled={unblockingBot}
              className="shrink-0 bg-white text-red-600 hover:bg-white/90 text-xs font-bold px-2.5 py-1 rounded transition-colors shadow-xs"
            >
              Unblock Bot
            </button>
          )}
        </div>
      )}

      {/* Main chat area */}
      <div className="relative flex flex-1 min-h-0 w-full border-0 md:border md:border-border rounded-none md:rounded-lg overflow-hidden bg-background">
        {/* User list — hidden on mobile when chat is open and not in bulk mode */}
        <div className={`${activeUser && !bulkMode ? 'hidden md:flex' : 'flex'} ${fullscreen ? 'w-full md:w-56' : 'w-full md:w-72 lg:w-80'} border-r border-border flex-col shrink-0 bg-background min-h-0`}>
          {/* Left panel header with Title, Fullscreen, Bulk, Channels, Status tabs */}
          <div className="p-3 border-b border-border shrink-0 space-y-2.5">
            {/* Top row: All Live Chat title + Fullscreen + Bulk Send */}
            <div className="flex items-center justify-between gap-1 pl-11 lg:pl-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  All Live Chat
                </h3>
                {chatUsers.reduce((s, x) => s + (x.unread || 0), 0) > 0 && (
                  <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[18px] text-center shadow-xs animate-in zoom-in-95">
                    {chatUsers.reduce((s, x) => s + (x.unread || 0), 0) > 99 ? '99+' : chatUsers.reduce((s, x) => s + (x.unread || 0), 0)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFullscreen(!fullscreen)}
                  className="h-7 px-2 text-xs gap-1 hover:bg-accent text-muted-foreground hover:text-foreground"
                  data-testid="fullscreen-toggle-btn"
                  title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {fullscreen ? <ArrowsInSimple size={13} /> : <ArrowsOutSimple size={13} />}
                  <span className="hidden sm:inline text-[11px]">{fullscreen ? 'Exit' : 'Full'}</span>
                </Button>
                <Button
                  variant={bulkMode ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setBulkMode(!bulkMode);
                    setSelectedUsers([]);
                    setBulkFile(null);
                    setBulkMediaUrl(null);
                    setBulkMediaType(null);
                    setBulkButtons([]);
                    setShowBulkExtras(false);
                  }}
                  className={`h-7 px-2 text-xs gap-1 ${bulkMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="bulk-mode-btn"
                  title={bulkMode ? 'Exit Bulk' : 'Bulk Send'}
                >
                  <UsersThree size={13} />
                  <span className="text-[11px]">{bulkMode ? 'Exit Bulk' : 'Bulk'}</span>
                </Button>
              </div>
            </div>

            {/* Channels Filter: All | Telegram | WhatsApp */}
            <div className="flex gap-1 p-0.5 bg-muted rounded-md text-xs">
              {(() => {
                const totalAll = chatUsers.reduce((s, x) => s + (x.unread || 0), 0);
                const totalTg = chatUsers.filter(x => x.source !== 'whatsapp').reduce((s, x) => s + (x.unread || 0), 0);
                const totalWa = chatUsers.filter(x => x.source === 'whatsapp').reduce((s, x) => s + (x.unread || 0), 0);
                return (
                  <>
                    <button onClick={() => { setChannelFilter('all'); setActiveUser(null); }}
                      className={`flex-1 text-[11px] py-1 rounded font-medium transition-colors flex items-center justify-center gap-1 ${channelFilter==='all' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                      <span>All</span>
                      {totalAll > 0 && <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] text-center">{totalAll}</span>}
                    </button>
                    <button onClick={() => { setChannelFilter('telegram'); setActiveUser(null); }}
                      className={`flex-1 text-[11px] py-1 rounded font-medium transition-colors flex items-center justify-center gap-1 ${channelFilter==='telegram' ? 'bg-[#0088cc] text-white shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}>
                      <span>Telegram</span>
                      {totalTg > 0 && <span className="px-1.5 py-0.2 bg-white text-[#0088cc] text-[9px] font-bold rounded-full min-w-[16px] text-center">{totalTg}</span>}
                    </button>
                    <button onClick={() => { setChannelFilter('whatsapp'); setActiveUser(null); }}
                      className={`flex-1 text-[11px] py-1 rounded font-medium transition-colors flex items-center justify-center gap-1 ${channelFilter==='whatsapp' ? 'bg-[#25D366] text-white shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}>
                      <span>WhatsApp</span>
                      {totalWa > 0 && <span className="px-1.5 py-0.2 bg-white text-[#25D366] text-[9px] font-bold rounded-full min-w-[16px] text-center">{totalWa}</span>}
                    </button>
                  </>
                );
              })()}
            </div>

            {/* Status tabs: Open | Closed | Assigned | Blocked */}
            <div className="flex gap-1 p-0.5 bg-muted rounded-md text-xs">
              {(() => {
                const openUnreadCount = chatUsers.filter(x => (x.status === 'open' || !x.status) && !x.is_blocked).reduce((s, x) => s + (x.unread || 0), 0);
                return (
                  <button data-testid="all-chats-tab-open" onClick={() => { setStatusFilter('open'); setActiveUser(null); }}
                    className={`flex-1 text-[11px] py-1 rounded font-medium transition-colors flex items-center justify-center gap-1 ${statusFilter==='open' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                    <span>Open</span>
                    {openUnreadCount > 0 && (
                      <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] text-center">
                        {openUnreadCount}
                      </span>
                    )}
                  </button>
                );
              })()}
              <button data-testid="all-chats-tab-closed" onClick={() => { setStatusFilter('closed'); setActiveUser(null); }}
                className={`flex-1 text-[11px] py-1 rounded font-medium transition-colors ${statusFilter==='closed' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>Closed</button>
              <button data-testid="all-chats-tab-assigned" onClick={() => { setStatusFilter('assigned'); setActiveUser(null); }}
                className={`flex-1 text-[11px] py-1 rounded font-medium transition-colors flex items-center justify-center gap-1 ${statusFilter==='assigned' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                <span>Assigned</span>
                {assignedUnreadCount > 0 && (
                  <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] text-center">
                    {assignedUnreadCount}
                  </span>
                )}
              </button>
              {user?.role === 'super_admin' && (
                <button data-testid="all-chats-tab-blocked" onClick={() => { setStatusFilter('blocked'); setActiveUser(null); }}
                  className={`flex-1 text-[11px] py-1 rounded font-medium transition-colors ${statusFilter==='blocked' ? 'bg-red-500 text-white shadow-xs' : 'text-muted-foreground hover:text-red-500'}`}>Blocked</button>
              )}
            </div>

            {/* Realtime Instant Filter Search */}
            <div className="relative">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search chats by name, @username, phone..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setVisibleLimit(50); }}
                className="w-full pl-7 pr-6 py-1 bg-muted/60 hover:bg-muted/90 focus:bg-background border border-border/70 rounded-md text-[11px] placeholder:text-muted-foreground/60 transition-colors outline-none focus:ring-1 focus:ring-primary h-7"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setVisibleLimit(50); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
                >
                  <X size={11} />
                </button>
              )}
            </div>

            {/* List header info */}
            {bulkMode ? (
              <div className="flex items-center justify-between pt-0.5">
                <span className="text-[11px] font-medium text-muted-foreground">Select users</span>
                <Button variant="ghost" size="sm" onClick={selectAllUsers} className="h-6 text-[11px] px-2 gap-1 text-primary hover:text-primary" data-testid="bulk-select-all">
                  <CheckSquare size={13} /> {selectedUsers.length === chatUsers.filter(u => u.source !== 'whatsapp').length ? 'Deselect All' : 'Select All'} ({selectedUsers.length})
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between text-[11px] text-muted-foreground px-0.5">
                <span className="font-semibold uppercase tracking-wider text-[10px]">Chats ({filteredUsers.length})</span>
                {(() => {
                  const totalUnreadInList = filteredUsers.reduce((s, x) => s + (x.unread || 0), 0);
                  return totalUnreadInList > 0 ? (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center gap-1 shadow-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      {totalUnreadInList} {totalUnreadInList === 1 ? 'unread' : 'unreads'}
                    </span>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          {/* User list with progressive rendering for instant 60fps speed */}
          <div className="flex-1 overflow-y-auto" onScroll={handleUserListScroll}>
            {displayedUsers.map(u => {
              const isWaEntry = u.source === 'whatsapp';
              const key = getChatKey(u);
              const active = getChatKey(activeUser) === key && !bulkMode;
              const hasUnread = (u.unread || 0) > 0;

              const handleClick = () => {
                if (bulkMode && !isWaEntry) return toggleUserSelection(u.telegram_user_id);
                if (!hasHistoryPushedRef.current) {
                  window.history.pushState({ botLiveChatOpen: true }, '');
                  hasHistoryPushedRef.current = true;
                }
                const currentKey = activeUserRef.current ? getChatKey(activeUserRef.current) : null;
                const newKey = getChatKey(u);
                if (currentKey !== newKey) {
                  const cached = chatCacheRef.current.get(newKey);
                  if (cached && cached.length > 0) {
                    setMessages(cached);
                    setIsLoadingChat(false);
                  } else {
                    setMessages([]);
                    setIsLoadingChat(true);
                  }
                }
                setActiveUser(u);
                if (u.unread > 0) {
                  const targetBid = u.bot_id || selectedBot;
                  setChatUsers(prev => sortChatsWithUnreadFirst(prev.map(item => {
                    return getChatKey(item) === newKey ? { ...item, unread: 0 } : item;
                  }), newKey));
                  setAssignedUnreadCount(prev => Math.max(0, prev - (u.unread || 1)));

                  // Immediately update read status on server
                  if (isWaEntry) {
                    API.post(`/whatsapp/contacts/${u.contact_id}/read`).catch(() => {});
                  } else if (targetBid) {
                    API.post(`/bot-chat/${targetBid}/read/${u.telegram_user_id}`).catch(() => {});
                  }
                  window.dispatchEvent(new CustomEvent('chat-read'));
                }
              };
              const isAssignedToAdmin = user?.role === 'staff' && u.assigned_to_admin_id && u.assigned_to_admin_id !== user.id;

              return (
                <button key={key} onClick={handleClick}
                  data-testid={isWaEntry ? `wa-list-${u.contact_id}` : `bot-chat-user-${u.telegram_user_id}`}
                  className={`w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-accent/50 transition-colors flex items-center gap-2.5 ${active ? 'bg-accent' : (hasUnread ? 'bg-red-500/[0.04] dark:bg-red-500/[0.08] border-l-2 border-l-red-500' : '')}`}>
                  {bulkMode && !isWaEntry && <Checkbox checked={selectedUsers.includes(u.telegram_user_id)} className="shrink-0" />}
                  <div className="relative shrink-0">
                    <div className={`w-8 h-8 rounded-full ${isWaEntry ? 'bg-green-500/20 text-green-600 dark:text-green-400' : 'bg-blue-500/20 text-blue-600 dark:text-blue-400'} flex items-center justify-center text-[11px] font-bold`}>
                      {(u.name || '?')[0].toUpperCase()}
                    </div>
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ${isWaEntry ? 'bg-green-500' : 'bg-[#229ED9]'} border-2 border-background flex items-center justify-center`} title={isWaEntry ? 'WhatsApp' : 'Telegram'}>
                      {isWaEntry ? <WhatsappLogo size={8} weight="fill" className="text-white" /> : <TelegramLogo size={8} weight="fill" className="text-white" />}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 mb-0.5">
                      <span className={`text-[13px] truncate ${hasUnread ? 'font-bold text-foreground' : 'font-medium'}`}>{u.name}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {(u.is_blocked || u.status === 'blocked') && (
                          <span className="px-1.5 py-0.2 bg-red-500/15 text-red-600 border border-red-500/20 text-[9px] rounded font-semibold flex items-center gap-0.5">
                            <Prohibit size={10} weight="bold" /> Blocked
                          </span>
                        )}
                        {isAssignedToAdmin && (
                          <span className="px-1.5 py-0.2 bg-amber-500/10 text-amber-600 border border-amber-500/20 text-[9px] rounded font-semibold">
                            Admin
                          </span>
                        )}
                        {hasUnread && (
                          <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center text-center shrink-0 shadow-xs animate-in zoom-in-95">
                            {u.unread > 99 ? '99+' : u.unread}
                          </span>
                        )}
                      </div>
                    </div>
                    {isWaEntry ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-[9px] text-green-700/70 dark:text-green-400/70 font-mono">+{u.phone}</p>
                        <WhatsApp24hListBadge lastIncomingTime={u.last_incoming_time || u.last_time} />
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {u.username && <span className="text-[9px] text-muted-foreground font-mono">@{u.username}</span>}
                        {(u.bot_username || u.bot_name) && (
                          <span className="text-[9px] text-primary/80 font-mono font-medium">
                            🤖 @{u.bot_username || u.bot_name}
                          </span>
                        )}
                      </div>
                    )}
                    <p className={`text-[10px] truncate ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{u.last_message || '...'}</p>
                  </div>
                </button>
              );
            })}
            {filteredUsers.length === 0 && (
              <div className="p-8 text-center text-xs text-muted-foreground">
                <p className="font-semibold text-foreground/80">{searchQuery ? 'No matching chats' : 'No chats yet'}</p>
                {searchQuery && <p className="text-[11px] mt-1 opacity-70">Check spelling or try a different term</p>}
              </div>
            )}
            {filteredUsers.length > displayedUsers.length && (
              <div className="py-2.5 text-center text-[10px] text-muted-foreground bg-muted/20 border-t border-border/30 font-medium">
                Showing {displayedUsers.length} of {filteredUsers.length} chats • Scroll down for more
              </div>
            )}
          </div>
        </div>

        {/* Right Area: Bulk Send Composer OR 1-on-1 Chat OR Empty State */}
        <div className={`${!activeUser && !bulkMode ? 'hidden md:flex' : 'flex'} flex-1 flex-col min-w-0 min-h-0 bg-background`}>
          {bulkMode ? (
            <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-background" data-testid="bulk-send-bar">
              {/* Bulk Header */}
              <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/20 shrink-0">
                <div className="flex items-center gap-2">
                  <UsersThree size={20} className="text-primary" />
                  <div>
                    <h4 className="text-sm font-semibold">Bulk Message Broadcast</h4>
                    <p className="text-[11px] text-muted-foreground">
                      {selectedUsers.length} user{selectedUsers.length === 1 ? '' : 's'} selected on Telegram
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={sendBulkMessage}
                    disabled={bulkSending || (!bulkText.trim() && !bulkFile && !bulkMediaUrl && bulkButtons.length === 0) || selectedUsers.length === 0}
                    className="gap-1.5"
                    data-testid="bulk-send-btn"
                  >
                    <PaperPlaneTilt size={14} /> Send ({selectedUsers.length})
                  </Button>
                </div>
              </div>

              {/* Bulk Scrollable Form */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="text-xs text-muted-foreground bg-muted/40 border border-border/60 rounded-md p-2.5">
                  <strong>Delivery Order:</strong> Image/Video → Text Message → Inline Buttons (sent sequentially to each user).
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold">Message Text</label>
                  <textarea
                    value={bulkText}
                    onChange={e => setBulkText(e.target.value)}
                    placeholder="Type your message to broadcast..."
                    rows={4}
                    className="w-full p-3 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                    data-testid="bulk-text-input"
                  />
                </div>

                {/* Media Attachment */}
                <div className="space-y-2 border border-border/70 rounded-lg p-3 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold">Media Attachment (Optional)</span>
                    <input ref={bulkFileRef} type="file" className="hidden" accept="image/*,video/*,.gif" onChange={e => { if (e.target.files[0]) { setBulkFile(e.target.files[0]); setBulkMediaUrl(null); setBulkMediaType(null); } e.target.value = ''; }} />
                  </div>
                  {bulkFile ? (
                    <div className="flex items-center gap-2 p-2 rounded-md bg-background border border-border">
                      {bulkFile.type?.startsWith('image') ? <Image size={18} className="text-blue-500" /> : <VideoCamera size={18} className="text-purple-500" />}
                      <span className="text-xs font-medium truncate flex-1">{bulkFile.name}</span>
                      <button type="button" onClick={() => setBulkFile(null)} className="text-destructive p-1 hover:bg-destructive/10 rounded"><X size={14} /></button>
                    </div>
                  ) : bulkMediaUrl ? (
                    <div className="flex items-center gap-2 p-2 rounded-md bg-background border border-border">
                      <span className="text-xs font-medium truncate flex-1">Saved {bulkMediaType || 'media'}</span>
                      <button type="button" onClick={() => { setBulkMediaUrl(null); setBulkMediaType(null); }} className="text-destructive p-1 hover:bg-destructive/10 rounded"><X size={14} /></button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="bulk-add-image" onClick={() => { bulkFileRef.current.accept = 'image/*'; bulkFileRef.current?.click(); }}>
                        <Image size={14} className="text-blue-500" /> Image
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="bulk-add-video" onClick={() => { bulkFileRef.current.accept = 'video/*'; bulkFileRef.current?.click(); }}>
                        <VideoCamera size={14} className="text-purple-500" /> Video
                      </Button>
                    </div>
                  )}
                </div>

                {/* Inline Buttons */}
                <div className="space-y-2.5 border border-border/70 rounded-lg p-3 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold">Inline Buttons (Optional)</span>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="bulk-add-button" onClick={() => setBulkButtons([...bulkButtons, { title: '', url: '' }])}>
                        <Link size={13} /> Add Button
                      </Button>
                      {bulkButtons.length > 0 && <button type="button" onClick={() => setBulkButtons([])} className="text-destructive text-xs hover:underline">Clear</button>}
                    </div>
                  </div>
                  {bulkButtons.map((btn, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input value={btn.title} onChange={e => { const b = [...bulkButtons]; b[i].title = e.target.value; setBulkButtons(b); }} placeholder="Button Label" className="h-8 text-xs flex-1" data-testid={`bulk-btn-title-${i}`} />
                      <Input value={btn.url} onChange={e => { const b = [...bulkButtons]; b[i].url = e.target.value; setBulkButtons(b); }} placeholder="https://..." className="h-8 text-xs flex-1" data-testid={`bulk-btn-url-${i}`} />
                      <button type="button" onClick={() => setBulkButtons(bulkButtons.filter((_, j) => j !== i))} className="text-destructive p-1 hover:bg-destructive/10 rounded"><Trash size={14} /></button>
                    </div>
                  ))}
                  {bulkButtons.length > 0 && (
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-xs text-muted-foreground">Show button label text</span>
                      <Switch data-testid="bulk-show-btn-label" checked={bulkShowLabel} onCheckedChange={setBulkShowLabel} className="scale-75" />
                    </div>
                  )}
                </div>

                {/* Templates and Save */}
                <div className="flex items-center gap-2 pt-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowBulkExtras(!showBulkExtras)} className="gap-1 text-xs h-8" data-testid="bulk-extras-toggle">
                    <Plus size={14} /> {showBulkExtras ? 'Less Options' : 'More Options'}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowBulkTemplates(!showBulkTemplates)} className="gap-1.5 text-xs h-8" data-testid="bulk-templates-toggle">
                    <FolderOpen size={14} /> Saved ({bulkTemplates.length})
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowBulkSave(!showBulkSave)} className="gap-1.5 text-xs h-8" data-testid="bulk-save-toggle">
                    <BookmarkSimple size={14} /> Save Template
                  </Button>
                </div>

                {showBulkSave && (
                  <div className="flex gap-2 p-2.5 rounded-lg border border-border bg-muted/30" data-testid="bulk-save-section">
                    <Input value={bulkSaveName} onChange={e => setBulkSaveName(e.target.value)} placeholder="Template name..." className="flex-1 h-8 text-xs" data-testid="bulk-save-name" />
                    <Button size="sm" onClick={saveBulkTemplate} className="h-8 gap-1 text-xs" data-testid="bulk-save-confirm"><FloppyDisk size={12} /> Save</Button>
                  </div>
                )}

                {showBulkTemplates && (
                  <div className="space-y-1.5 border border-border rounded-lg p-3 bg-muted/20" data-testid="bulk-templates-list">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Saved Templates</p>
                    {bulkTemplates.length === 0 ? <p className="text-xs text-muted-foreground py-2 text-center">No saved templates</p> :
                      bulkTemplates.map(tpl => (
                        <div key={tpl.id} className="flex items-center gap-2 border border-border rounded px-2.5 py-2 bg-background hover:bg-accent/40 transition-colors">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{tpl.name}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{tpl.text || 'No text'}</p>
                          </div>
                          <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => loadBulkTemplate(tpl)}>Load</Button>
                          <button onClick={() => deleteBulkTemplate(tpl.id)} className="text-destructive p-1 hover:bg-destructive/10 rounded"><Trash size={12} /></button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          ) : activeUser ? (
            <>
              {/* Chat header — fixed/sticky on mobile and desktop */}
              <div className="sticky top-0 z-30 px-3 md:px-4 py-2 border-b border-border flex items-center gap-2 shrink-0 bg-background/95 backdrop-blur shadow-xs min-h-[52px]">
                <button
                  type="button"
                  onClick={handleBack}
                  className="md:hidden p-1.5 -ml-1 rounded-full hover:bg-accent text-foreground flex items-center justify-center transition-colors touch-manipulation active:scale-95 shrink-0"
                  data-testid="chat-back-btn"
                  aria-label="Back to chat list (Esc)"
                  title="Back (Esc)"
                >
                  <CaretLeft size={22} weight="bold" />
                </button>
                <div className="relative shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isWA ? 'bg-green-500/20 text-green-600 dark:text-green-400' : 'bg-blue-500/20 text-blue-600 dark:text-blue-400'}`}>
                    {(activeUser.name || '?')[0].toUpperCase()}
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ${isWA ? 'bg-green-500' : 'bg-[#229ED9]'} border-2 border-background flex items-center justify-center`} title={isWA ? 'WhatsApp' : 'Telegram'}>
                    {isWA ? <WhatsappLogo size={8} weight="fill" className="text-white" /> : <TelegramLogo size={8} weight="fill" className="text-white" />}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  {isEditingName ? (
                    <form onSubmit={handleSaveContactName} className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={editNameValue}
                        onChange={e => setEditNameValue(e.target.value)}
                        className="text-xs px-2 py-1 rounded border border-primary bg-background focus:outline-none max-w-[170px] sm:max-w-[220px]"
                        autoFocus
                        placeholder="Enter name..."
                      />
                      <button
                        type="submit"
                        disabled={savingName}
                        className="p-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                        title="Save name"
                      >
                        <Check size={13} weight="bold" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditingName(false)}
                        className="p-1 rounded border border-border hover:bg-muted text-muted-foreground transition-colors"
                        title="Cancel"
                      >
                        <X size={13} />
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-sm font-semibold leading-tight truncate">{activeUser.name}</p>
                      <button
                        type="button"
                        onClick={() => {
                          setEditNameValue(activeUser.name || '');
                          setIsEditingName(true);
                        }}
                        className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors shrink-0"
                        title="Edit name"
                        aria-label="Edit name"
                      >
                        <PencilSimple size={13} />
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mt-0.5">
                    {/* User username / ID */}
                    <span className="text-[10px] text-muted-foreground font-mono truncate">
                      {isWA ? `+${activeUser.phone}` : (activeUser.username ? `@${activeUser.username}` : `ID: ${activeUser.display_telegram_user_id || activeUser.telegram_user_id}`)}
                    </span>

                    {/* Bot Name & Username badge */}
                    {!isWA && (activeUser.bot_username || activeUser.bot_name || bot?.bot_username || bot?.bot_name) && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 shrink-0" title="Bot Name & Username">
                        <TelegramLogo size={10} weight="fill" />
                        <span className="truncate max-w-[160px]">
                          {activeUser.bot_name || bot?.bot_name ? `${activeUser.bot_name || bot?.bot_name} ` : ''}
                          (@{activeUser.bot_username || bot?.bot_username || 'bot'})
                        </span>
                      </span>
                    )}

                    {isWA && activeUser.api_title && (
                      <span className="px-1 bg-green-500/10 text-green-700 dark:text-green-300 rounded text-[9px] font-medium">
                        {activeUser.api_title}
                      </span>
                    )}

                    {/* WhatsApp 24h badge */}
                    {isWA && (
                      <WhatsApp24hHeaderBadge
                        isWA={true}
                        lastIncomingTime={activeUser?.last_incoming_time || activeUser?.last_time}
                        waTimer={waTimer}
                      />
                    )}
                  </div>
                </div>

                {/* Assignment & Status Controls */}
                <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                  {user?.role !== 'staff' ? (
                    <>
                      <select
                        value={activeUser.assigned_to_admin_id || ''}
                        onChange={(e) => handleAssign(e.target.value)}
                        className="bg-transparent border border-border text-[11px] rounded px-1.5 py-1 text-muted-foreground hover:bg-accent max-w-[90px] sm:max-w-[130px] truncate"
                      >
                        <option value="">Unassigned</option>
                        {adminList.map(a => (
                          <option key={a.id} value={a.id}>{a.label || a.name}</option>
                        ))}
                        {user?.id && <option value={user.id}>Myself ({user.name})</option>}
                      </select>

                      {activeUser.assigned_to_admin_id ? (
                        <button
                          onClick={() => handleAssign('')}
                          data-testid="unassign-btn"
                          title="Unassign chat"
                          className="hidden sm:inline-flex px-2 py-1 rounded-md text-[11px] font-medium border border-border bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
                        >
                          Unassign
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <select
                        value={activeUser.assigned_to_admin_id || user.id}
                        onChange={(e) => handleAssign(e.target.value)}
                        className="bg-transparent border border-border text-[11px] rounded px-1.5 py-1 text-muted-foreground hover:bg-accent max-w-[90px] sm:max-w-[130px] truncate"
                      >
                        <option value={user.id}>Myself ({user.name})</option>
                        {adminList.filter(a => a.id !== user.id).map(a => (
                          <option key={a.id} value={a.id}>{a.label || a.name || 'Admin'}</option>
                        ))}
                      </select>

                      {activeUser.assigned_to_admin_id && activeUser.assigned_to_admin_id !== user.id && (
                        <button
                          onClick={() => handleAssign(user.id)}
                          data-testid="rollback-to-me-btn"
                          title="Rollback assignment to me"
                          className="px-2 py-1 rounded-md text-[11px] font-medium border border-amber-500/40 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 transition-colors shrink-0 flex items-center gap-1"
                        >
                          <ArrowCounterClockwise size={12} weight="bold" /> <span className="hidden sm:inline">Rollback</span>
                        </button>
                      )}
                    </>
                  )}

                  {/* Super Admin Block / Unblock User Button */}
                  {user?.role === 'super_admin' && (
                    (activeUser.is_blocked || activeUser.status === 'blocked') ? (
                      <button
                        onClick={handleUnblockActiveUser}
                        disabled={blockingUser}
                        data-testid="livechat-unblock-user-btn"
                        title="Unblock user as Super Admin"
                        className="px-2 py-1 rounded-md text-[11px] font-medium border border-amber-500/40 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 transition-colors shrink-0 flex items-center gap-1"
                      >
                        <ShieldCheck size={13} weight="bold" />
                        <span className="hidden sm:inline">Unblock</span>
                      </button>
                    ) : (
                      <button
                        onClick={handleBlockActiveUser}
                        disabled={blockingUser}
                        data-testid="livechat-block-user-btn"
                        title="Block user from Super Admin"
                        className="px-2 py-1 rounded-md text-[11px] font-medium border border-red-500/40 bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors shrink-0 flex items-center gap-1"
                      >
                        <Prohibit size={13} weight="bold" />
                        <span className="hidden sm:inline">Block</span>
                      </button>
                    )
                  )}

                  {canDeleteChat && (
                      <button
                        onClick={() => {
                          setUserToDelete(activeUser);
                          setConfirmDeleteChatOpen(true);
                        }}
                        data-testid="chat-delete-btn"
                        title="Delete chat permanently"
                        className="px-2 py-1 rounded-md text-[11px] font-medium border border-red-500/40 bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors shrink-0 flex items-center gap-1"
                      >
                        <Trash size={13} />
                        <span className="hidden sm:inline">Delete Chat</span>
                      </button>
                    )}
                    <button onClick={toggleContactStatus} data-testid="chat-close-toggle" title={activeUser.status === 'closed' ? 'Reopen chat' : 'Close chat'}
                    className={`px-2 py-1 rounded-md text-[11px] font-medium flex items-center gap-1 transition-colors ${activeUser.status === 'closed' ? 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20'}`}>
                    {activeUser.status === 'closed' ? (<><ArrowClockwise size={13} /> <span className="hidden sm:inline">Reopen</span></>) : (<><CheckCircle size={13} /> <span className="hidden sm:inline">Close</span></>)}
                  </button>
                  <button onClick={() => setActiveUser(null)} data-testid="close-chat-btn" title="Hide chat"
                    className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                    <XCircle size={19} />
                  </button>
                </div>
              </div>

              {/* Blocked user warning notification */}
              {(activeUser.is_blocked || activeUser.status === 'blocked') && (
                <div className="bg-red-500/10 border-b border-red-500/30 text-red-600 dark:text-red-400 px-3 py-1.5 text-xs flex items-center justify-between gap-2 shrink-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Prohibit size={14} weight="bold" className="shrink-0" />
                    <span className="truncate font-medium">This user is blocked by Super Admin. Incoming and outgoing messages are disabled.</span>
                  </div>
                  {user?.role === 'super_admin' && (
                    <button onClick={handleUnblockActiveUser} disabled={blockingUser} className="underline text-[11px] font-bold shrink-0 hover:text-red-700">
                      Unblock
                    </button>
                  )}
                </div>
              )}

              {/* Messages Scroll Area */}
              <div
                ref={chatContainerRef}
                onScroll={handleChatScroll}
                className={`flex-1 overflow-y-auto px-3 md:px-4 py-3 space-y-2 chat-scroll-container overscroll-contain ${isWA ? 'bg-[#efeae2] dark:bg-[#0b141a]' : 'bg-[#f0f0f0] dark:bg-[#0e1621]'}`}
                data-testid="bot-chat-messages"
              >
                {isLoadingChat ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[220px] gap-2.5 text-muted-foreground select-none">
                    <div className="w-7 h-7 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                    <p className="text-xs font-medium tracking-wide">Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[220px] gap-2 text-muted-foreground select-none py-12">
                    <span className="text-2xl">💬</span>
                    <p className="text-xs font-medium">No messages yet</p>
                  </div>
                ) : (
                  messages.map(renderMessage)
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* File preview */}
              {file && (
                <div className="flex items-center gap-2 p-2 mx-3 mb-1 border border-border rounded-lg bg-background shadow-sm" data-testid="chat-file-preview">
                  {file.type?.startsWith('image') ? <img src={URL.createObjectURL(file)} alt="" className="w-12 h-12 rounded-lg object-cover" /> :
                   file.type?.startsWith('video') ? <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center"><VideoCamera size={20} /></div> :
                   <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center text-xs font-bold">{file.name.split('.').pop()?.toUpperCase()}</div>}
                  <span className="text-xs truncate flex-1">{file.name}</span>
                  <button onClick={() => setFile(null)} className="text-destructive p-1"><X size={16} /></button>
                </div>
              )}

              {/* WhatsApp 24-Hour Window Expired Warning Alert */}
              {isWA && <WhatsApp24hExpiredAlert isExpired={waTimer.isExpired} />}

              {/* Input area */}
              {(activeUser.is_blocked || activeUser.status === 'blocked') ? (
                <div className="bg-red-50 dark:bg-red-950/40 border-t border-red-200 dark:border-red-900/50 shrink-0 p-3 text-center text-xs text-red-600 dark:text-red-400 font-medium pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] flex items-center justify-center gap-1.5">
                  <Prohibit size={15} weight="bold" />
                  <span>This user is blocked by Super Admin. Unblock user to send messages.</span>
                </div>
              ) : activeUser.status === 'closed' ? (
                <div className="bg-background border-t border-border shrink-0 p-3 text-center text-xs text-muted-foreground pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
                  This chat is closed. Reopen it to reply.
                </div>
              ) : (
                <div className="sticky bottom-0 z-30 bg-background border-t border-border shrink-0 pb-[env(safe-area-inset-bottom,0px)]">
                  <div className="flex items-end gap-1.5 sm:gap-2 p-2 sm:p-2.5 max-w-full">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/*,video/*,audio/*,.gif,.pdf,.doc,.docx,.txt,.zip"
                      onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); e.target.value = ''; }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="bot-chat-attach"
                      className="shrink-0 mb-0.5 rounded-full w-9 h-9 p-0 hover:bg-muted text-muted-foreground hover:text-foreground touch-manipulation"
                    >
                      <Paperclip size={20} />
                    </Button>
                    <textarea
                      ref={textareaRef}
                      data-testid="bot-chat-input"
                      value={text}
                      onChange={e => {
                        setText(e.target.value);
                        adjustTextareaHeight(e.target);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder="Write a message..."
                      className="flex-1 resize-none bg-muted/40 hover:bg-muted/60 focus:bg-background border border-border/80 focus:border-primary rounded-2xl px-3.5 py-2 text-sm focus:outline-none transition-colors min-h-[38px] max-h-[120px] overflow-y-auto leading-relaxed"
                      rows={1}
                      style={{ height: '38px' }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => sendReply()}
                      disabled={sending || (!text?.trim() && !file)}
                      data-testid="bot-chat-send"
                      className="shrink-0 rounded-full w-9 h-9 p-0 mb-0.5 shadow-sm active:scale-95 transition-transform touch-manipulation bg-primary text-primary-foreground"
                    >
                      <PaperPlaneTilt size={18} weight="fill" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground bg-background hidden md:flex">
              <div className="text-center px-4">
                <p className="text-4xl mb-3">💬</p>
                <p className="text-sm">Select a user to chat</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Media preview dialog */}
      <Dialog open={!!mediaPreview} onOpenChange={() => setMediaPreview(null)}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-1 bg-black/95 border-none">
          {mediaPreview?.type === 'photo' && (
            <img src={mediaPreview.url} alt="" className="max-w-full max-h-[85vh] object-contain mx-auto rounded" />
          )}
          {mediaPreview?.type === 'video' && (
            <video src={mediaPreview.url} controls autoPlay className="max-w-full max-h-[85vh] mx-auto rounded" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  return (
    <div className={`flex flex-col flex-1 min-h-0 w-full ${fullscreen ? 'fixed inset-0 z-50 bg-background p-2' : 'h-full max-h-full overflow-hidden'}`} data-testid="bot-live-chat-page">
      {/* Inject fullscreen CSS */}
      <style>{`.livechat-fullscreen > aside { display: none !important; } .livechat-fullscreen > main > div { padding: 0 !important; max-width: none !important; }`}</style>
      {chatContent}
      {/* Delete Chat Confirmation Dialog */}
      <Dialog open={confirmDeleteChatOpen} onOpenChange={setConfirmDeleteChatOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash size={18} /> Delete Chat Permanently
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2 text-sm text-muted-foreground">
            <p>
              Are you sure you want to permanently delete this chat with{' '}
              <strong className="text-foreground">{userToDelete?.name || userToDelete?.first_name || 'this user'}</strong>?
            </p>
            <p className="text-xs text-red-500 bg-red-500/10 p-2.5 rounded border border-red-500/20">
              ⚠️ <strong>Warning:</strong> All conversation messages and history with this user will be permanently erased from the database. This action cannot be undone.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t">
            <Button variant="outline" size="sm" onClick={() => { setConfirmDeleteChatOpen(false); setUserToDelete(null); }} disabled={deletingChat}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDeleteChat} disabled={deletingChat} className="gap-1.5" data-testid="confirm-delete-chat-btn">
              {deletingChat ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
